import { useNextRouterContext } from "@faire/deprecated-retailer-visitor-shared/app/_lib/routing/NextRouterContext";
import { useIsLoggedInRetailer } from "@faire/deprecated-retailer-visitor-shared/lib/isLoggedInRetailer";
import { useSettingNextJSRscCategoryPage } from "@faire/deprecated-retailer-visitor-shared/settings/useSettingNextJSRscCategoryPage";
import { setTimeoutIfPossible } from "@faire/next-utils/isomorphic/setTimeoutIfPossible";
import { PrefetchKind } from "next/dist/client/components/router-reducer/router-reducer-types";
import React from "react";

import { useSettingEnableCategoryPrefetchLinkToggle } from "../settings/getSettingEnableCategoryPrefetchLinkToggle";

type SurfaceType =
  | "categoriesForYou"
  | "filterToggle"
  | "headerDropdown"
  | "sideNav"
  | "topNav"
  | "visualNav"
  | "default";

export const useCategoryNavigationPrefetch = (surfaceType: SurfaceType) => {
  const isLoggedInRetailer = useIsLoggedInRetailer();
  const { prefetch: nextPrefetch } = useNextRouterContext();
  const isRSCCategoryPageEnabled = useSettingNextJSRscCategoryPage();

  const shouldEnablePrefetch = isLoggedInRetailer && isRSCCategoryPageEnabled;
  const timeoutRef = React.useRef<number | undefined>(undefined);
  const prefetchSettings = useSettingEnableCategoryPrefetchLinkToggle();

  const DEFAULT_SURFACE_SETTINGS = {
    enabled: false,
    threshold: 800,
  };
  const surfaceSettings =
    prefetchSettings.surfaces[surfaceType] || DEFAULT_SURFACE_SETTINGS;

  const prefetchKind = React.useMemo(() => {
    return surfaceSettings.enabled ? PrefetchKind.FULL : PrefetchKind.AUTO;
  }, [surfaceSettings.enabled]);

  const onMouseEnter = React.useCallback(
    (url?: string) => {
      if (shouldEnablePrefetch) {
        timeoutRef.current = setTimeoutIfPossible(() => {
          if (url) {
            nextPrefetch(url, {
              kind: prefetchKind,
            });
          }
        }, surfaceSettings.threshold);
      }
    },
    [
      nextPrefetch,
      shouldEnablePrefetch,
      surfaceSettings.threshold,
      prefetchKind,
    ]
  );

  const onTouchStart = React.useCallback(
    (url?: string) => {
      if (shouldEnablePrefetch && url) {
        nextPrefetch(url, {
          kind: prefetchKind,
        });
      }
    },
    [nextPrefetch, shouldEnablePrefetch, prefetchKind]
  );

  const onBlur = React.useCallback(() => {
    if (shouldEnablePrefetch) {
      clearTimeout(timeoutRef.current);
    }
  }, [shouldEnablePrefetch]);

  return {
    prefetchOnMouseEnter: onMouseEnter,
    prefetchOnTouchStart: onTouchStart,
    clearPrefetchTimeoutOnBlur: onBlur,
  };
};

// check if current url start with /category
export const isCategoryUrl = (url: string) => /^\/category\//.test(url);
